import { UserProfile, useUpdateProfile } from '../hooks/useProfile';

interface PreferencesCardProps {
  profile: UserProfile;
}

type Theme = 'light' | 'dark' | 'sepia' | 'system';
type FontSize = 'small' | 'medium' | 'large' | 'extra-large';

const themes: { value: Theme; label: string }[] = [
  { value: 'system', label: 'System' },
  { value: 'light', label: 'Light' },
  { value: 'dark', label: 'Dark' },
  { value: 'sepia', label: 'Sepia' },
];

const fontSizes: { value: FontSize; label: string }[] = [
  { value: 'small', label: 'Small' },
  { value: 'medium', label: 'Medium' },
  { value: 'large', label: 'Large' },
  { value: 'extra-large', label: 'Extra Large' },
];

export default function PreferencesCard({ profile }: PreferencesCardProps) {
  const updateProfile = useUpdateProfile();

  // Use profile values with defaults matching backend DEFAULT_READER_PREFERENCES
  const theme = profile.reader?.theme ?? 'system';
  const fontSize = profile.reader?.fontSize ?? 'medium';

  const handleThemeChange = async (newTheme: Theme) => {
    await updateProfile.mutateAsync({ reader: { theme: newTheme } });
  };

  const handleFontSizeChange = async (newSize: FontSize) => {
    await updateProfile.mutateAsync({ reader: { fontSize: newSize } });
  };

  return (
    <div className="bg-white/5 border border-white/10 rounded-xl p-6">
      <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
        <svg
          className="w-5 h-5 text-purple-400"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
          />
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
          />
        </svg>
        Reading Preferences
      </h2>

      <div className="space-y-6">
        {/* Theme */}
        <div>
          <label className="block text-sm text-purple-300 mb-2">Theme</label>
          <div className="flex flex-wrap gap-2">
            {themes.map((t) => (
              <button
                key={t.value}
                onClick={() => handleThemeChange(t.value)}
                disabled={updateProfile.isPending}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  theme === t.value
                    ? 'bg-purple-600 text-white'
                    : 'bg-slate-700 text-purple-300 hover:bg-slate-600'
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>

        {/* Font Size */}
        <div>
          <label className="block text-sm text-purple-300 mb-2">Font Size</label>
          <div className="flex flex-wrap gap-2">
            {fontSizes.map((f) => (
              <button
                key={f.value}
                onClick={() => handleFontSizeChange(f.value)}
                disabled={updateProfile.isPending}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  fontSize === f.value
                    ? 'bg-purple-600 text-white'
                    : 'bg-slate-700 text-purple-300 hover:bg-slate-600'
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
